import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, Edit2, Trash2, Car, Users, Calendar, Clock, MapPin, 
  DollarSign, Phone, Mail, Check, X, Loader2, CarTaxiFront,
  Plane, Building, Star, Settings, Bell, TruckIcon, UserCircle,
  FileText, CreditCard, MessageSquare, Map, Filter, Search,
  ChevronDown, ChevronRight, Award, AlertCircle, RefreshCw,
  Send, Eye, MoreVertical, Briefcase, Layers, Upload, UserPlus,
  Headphones, BellRing
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { taxiCarrier } from '@/lib/api';

// Lazy load components to improve performance
const ServiceAreaMap = lazy(() => import('@/components/taxi/ServiceAreaMap'));
const BookForOtherForm = lazy(() => import('@/components/taxi/BookForOtherForm'));
const CorporateAccountPanel = lazy(() => import('@/components/taxi/CorporateAccountPanel'));
const DispatcherPanel = lazy(() => import('@/components/taxi/DispatcherPanel'));

// Vehicle class icons and colors
const VEHICLE_CLASS_CONFIG = {
  economy: { icon: Car, color: 'bg-gray-100 text-gray-700', label: 'Economy' },
  vip: { icon: Award, color: 'bg-purple-100 text-purple-700', label: 'VIP' },
  komfort: { icon: Car, color: 'bg-blue-100 text-blue-700', label: 'Comfort' },
  suv: { icon: TruckIcon, color: 'bg-green-100 text-green-700', label: 'SUV' },
  business: { icon: Briefcase, color: 'bg-slate-100 text-slate-700', label: 'Business' },
  van: { icon: TruckIcon, color: 'bg-orange-100 text-orange-700', label: 'Van' },
  premium: { icon: Award, color: 'bg-amber-100 text-amber-700', label: 'Premium' },
  limousine: { icon: Award, color: 'bg-yellow-100 text-yellow-800', label: 'Limousine' },
  kleinbus: { icon: TruckIcon, color: 'bg-teal-100 text-teal-700', label: 'Minibus' },
  autobus: { icon: TruckIcon, color: 'bg-indigo-100 text-indigo-700', label: 'Bus' },
};

const LIMO_FEATURES = [
  'wifi', 'minibar', 'tv', 'soundSystem', 'privacyGlass', 'ledLights', 'sunroof',
  'usb', 'childSeat', 'wheelchair', 'redCarpet', 'champagne', 'bluetooth', 'climate', 'leatherSeats',
];

const EMPTY_VEHICLE = () => ({
  name: '',
  vehicle_class: 'limousine',
  brand: '',
  model: '',
  year: new Date().getFullYear(),
  license_plate: '',
  color: '',
  capacity_passengers: 8,
  capacity_luggage: 4,
  doors: 4,
  body_style: 'stretch',
  fuel_type: 'petrol',
  transmission: 'automatic',
  length_m: '',
  chassis_number: '',
  city: '',
  description: '',
  hourly_price: '',
  daily_price: '',
  currency: 'EUR',
  min_hours: 2,
  chauffeur_included: true,
  driver_name: '',
  driver_phone: '',
  features: [],
  images: [],
  is_active: true,
});

const TaxiLimousineTab = () => {
  const { t, i18n } = useTranslation();
  const limoSection = new URLSearchParams(window.location.search).get('section');
  const limoTabs = ['requests', 'trips', 'vehicles', 'areas', 'pricing', 'dispatcher', 'notifications', 'book-for-other', 'corporate', 'settings'];
  const [activeTab, setActiveTab] = useState(limoTabs.includes(limoSection) ? limoSection : 'vehicles');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [profile, setProfile] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleClasses, setVehicleClasses] = useState([]);
  const [priceProfiles, setPriceProfiles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState(null);
  const [ratingDetails, setRatingDetails] = useState(null);
  const [serviceAreas, setServiceAreas] = useState([]);
  
  // UI states
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);
  const [showPriceProfileDialog, setShowPriceProfileDialog] = useState(false);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [showBookForOtherDialog, setShowBookForOtherDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editingPriceProfile, setEditingPriceProfile] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestFilter, setRequestFilter] = useState('new');
  
  // Form states
  const [profileForm, setProfileForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: 'DE',
    tax_number: '',
    license_number: '',
    description: '',
    languages: ['de'],
    bank_name: '',
    iban: '',
    paypal_email: '',
    whatsapp: '',
    website: '',
    secondary_phone: '',
    notes: ''
  });
  
  const [vehicleForm, setVehicleForm] = useState(() => EMPTY_VEHICLE());
  
  const [priceProfileForm, setPriceProfileForm] = useState({
    name: '',
    vehicle_id: '',
    is_active: true,
    base_price: 0,
    price_per_km: 0,
    price_per_hour: 0,
    minimum_price: 0,
    max_distance_km: null,
    min_distance_km: null,
    max_passengers: null,
    service_types: ['transfer'],
    available_days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
    available_hours_start: '00:00',
    available_hours_end: '23:59',
    pickup_areas: [],
    dropoff_areas: []
  });
  
  const [offerForm, setOfferForm] = useState({
    vehicle_id: '',
    price: 0,
    currency: 'EUR',
    notes: ''
  });

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load vehicle classes
      const classesRes = await taxiCarrier.getVehicleClasses();
      setVehicleClasses(classesRes.data.vehicle_classes || []);
      
      // Load carrier profile
      const profileRes = await taxiCarrier.getProfile();
      const registered = !!profileRes.data?.is_registered;
      setIsRegistered(registered);
      if (registered && profileRes.data.profile) {
        setProfile(profileRes.data.profile);
        setProfileForm(profileRes.data.profile);
      }

      await Promise.all([
        loadVehicles(),
        registered ? loadPriceProfiles() : Promise.resolve(),
        registered ? loadStats() : Promise.resolve(),
        registered ? loadRequests() : Promise.resolve(),
        registered ? loadServiceAreas() : Promise.resolve(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      // Don't show error for profile not found - just show registration
      if (error.response?.status !== 400) {
        toast.error(t('common.error', 'Error loading data'));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const res = await taxiCarrier.getVehicles();
      setVehicles(res.data.vehicles || []);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const loadPriceProfiles = async () => {
    try {
      const res = await taxiCarrier.getPriceProfiles();
      setPriceProfiles(res.data.price_profiles || []);
    } catch (error) {
      console.error('Error loading price profiles:', error);
    }
  };

  const loadStats = async () => {
    try {
      const res = await taxiCarrier.getStats();
      setStats(res.data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadRequests = async () => {
    try {
      const res = await taxiCarrier.getRequests({ status: requestFilter === 'my_offers' ? 'with_my_offers' : undefined });
      setRequests(res.data.requests || []);
    } catch (error) {
      console.error('Error loading requests:', error);
    }
  };

  const loadTrips = async () => {
    try {
      const res = await taxiCarrier.getTrips({});
      setTrips(res.data.trips || []);
    } catch (error) {
      console.error('Error loading trips:', error);
    }
  };

  const loadServiceAreas = async () => {
    try {
      const res = await taxiCarrier.getServiceAreas();
      setServiceAreas(res.data.service_areas || []);
    } catch (error) {
      console.error('Error loading service areas:', error);
    }
  };

  const handleSaveServiceArea = async (areaData) => {
    try {
      await taxiCarrier.createServiceArea(areaData);
      loadServiceAreas();
      toast.success(t('taxi.areaSaved', 'Service area saved'));
    } catch (error) {
      console.error('Error saving service area:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleDeleteServiceArea = async (areaId) => {
    try {
      await taxiCarrier.deleteServiceArea(areaId);
      loadServiceAreas();
      toast.success(t('common.deleted'));
    } catch (error) {
      console.error('Error deleting service area:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const loadRatingDetails = async () => {
    try {
      const res = await taxiCarrier.getRatingDetails();
      setRatingDetails(res.data);
      setShowRatingDialog(true);
    } catch (error) {
      console.error('Error loading rating details:', error);
      toast.error(t('common.error'));
    }
  };

  // Profile handlers
  const handleSaveProfile = async () => {
    try {
      if (!profileForm.company_name || !profileForm.contact_name || !profileForm.email || !profileForm.phone || !profileForm.city || !profileForm.country) {
        toast.error(t('validation.fillRequired', 'Please fill all required fields'));
        return;
      }
      
      const res = await taxiCarrier.saveProfile(profileForm);
      setProfile(res.data.profile);
      setIsRegistered(true);
      setShowProfileDialog(false);
      toast.success(t('common.saved', 'Profile saved successfully'));
      loadData();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  // Vehicle handlers
  const handleSaveVehicle = async () => {
    try {
      if (!vehicleForm.name || !vehicleForm.brand || !vehicleForm.model || !vehicleForm.vehicle_class) {
        toast.error(t('validation.fillRequired', 'Please fill all required fields'));
        return;
      }

      const payload = {
        ...vehicleForm,
        year: Number(vehicleForm.year) || new Date().getFullYear(),
        capacity_passengers: Number(vehicleForm.capacity_passengers) || 1,
        capacity_luggage: Number(vehicleForm.capacity_luggage) || 0,
        doors: vehicleForm.doors === '' ? null : Number(vehicleForm.doors),
        length_m: vehicleForm.length_m === '' ? null : Number(vehicleForm.length_m),
        hourly_price: vehicleForm.hourly_price === '' ? null : Number(vehicleForm.hourly_price),
        daily_price: vehicleForm.daily_price === '' ? null : Number(vehicleForm.daily_price),
        min_hours: vehicleForm.min_hours === '' ? null : Number(vehicleForm.min_hours),
      };
      
      if (editingVehicle) {
        await taxiCarrier.updateVehicle(editingVehicle.id, payload);
        toast.success(t('common.updated', 'Vehicle updated'));
      } else {
        await taxiCarrier.createVehicle(payload);
        toast.success(t('common.added', 'Vehicle added'));
      }
      
      setShowVehicleDialog(false);
      setEditingVehicle(null);
      resetVehicleForm();
      loadVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleDeleteVehicle = async (id) => {
    if (!window.confirm(t('common.confirmDelete', 'Are you sure you want to delete?'))) return;
    
    try {
      await taxiCarrier.deleteVehicle(id);
      toast.success(t('common.deleted', 'Vehicle deleted'));
      loadVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleEditVehicle = (vehicle) => {
    setVehicleForm({
      ...EMPTY_VEHICLE(),
      ...vehicle,
      model: vehicle.model || '',
      license_plate: vehicle.license_plate || '',
      color: vehicle.color || '',
      features: vehicle.features || [],
      images: vehicle.images || [],
      length_m: vehicle.length_m ?? '',
      hourly_price: vehicle.hourly_price ?? '',
      daily_price: vehicle.daily_price ?? '',
      chassis_number: vehicle.chassis_number || '',
      city: vehicle.city || '',
      description: vehicle.description || '',
      driver_name: vehicle.driver_name || '',
      driver_phone: vehicle.driver_phone || '',
    });
    setEditingVehicle(vehicle);
    setShowVehicleDialog(true);
  };

  const resetVehicleForm = () => {
    setVehicleForm(EMPTY_VEHICLE());
  };

  // Price Profile handlers
  const handleSavePriceProfile = async () => {
    try {
      if (!priceProfileForm.name || !priceProfileForm.vehicle_id || !priceProfileForm.price_per_km) {
        toast.error(t('validation.fillRequired', 'Please fill all required fields'));
        return;
      }
      
      if (editingPriceProfile) {
        await taxiCarrier.updatePriceProfile(editingPriceProfile.id, priceProfileForm);
        toast.success(t('common.updated'));
      } else {
        await taxiCarrier.createPriceProfile(priceProfileForm);
        toast.success(t('common.added'));
      }
      
      setShowPriceProfileDialog(false);
      setEditingPriceProfile(null);
      resetPriceProfileForm();
      loadPriceProfiles();
    } catch (error) {
      console.error('Error saving price profile:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleDeletePriceProfile = async (id) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    
    try {
      await taxiCarrier.deletePriceProfile(id);
      toast.success(t('common.deleted'));
      loadPriceProfiles();
    } catch (error) {
      console.error('Error deleting price profile:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const resetPriceProfileForm = () => {
    setPriceProfileForm({
      name: '',
      vehicle_id: '',
      is_active: true,
      base_price: 0,
      price_per_km: 0,
      price_per_hour: 0,
      minimum_price: 0,
      max_distance_km: null,
      min_distance_km: null,
      max_passengers: null,
      service_types: ['transfer'],
      available_days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
      available_hours_start: '00:00',
      available_hours_end: '23:59',
      pickup_areas: [],
      dropoff_areas: []
    });
  };

  // Offer handlers
  const handleCreateOffer = async () => {
    try {
      if (!offerForm.vehicle_id || !offerForm.price) {
        toast.error(t('validation.fillRequired'));
        return;
      }
      
      await taxiCarrier.createOffer(selectedRequest.id, offerForm);
      toast.success(t('taxi.offerSent', 'Offer sent successfully'));
      setShowOfferDialog(false);
      setSelectedRequest(null);
      setOfferForm({ vehicle_id: '', price: 0, currency: 'EUR', notes: '' });
      loadRequests();
    } catch (error) {
      console.error('Error creating offer:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  // Status badge helper
  const getStatusBadge = (status) => {
    const config = {
      pending: { variant: 'outline', className: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
      confirmed: { variant: 'outline', className: 'bg-green-50 text-green-700 border-green-200' },
      in_progress: { variant: 'outline', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      completed: { variant: 'outline', className: 'bg-gray-50 text-gray-700 border-gray-200' },
      cancelled: { variant: 'outline', className: 'bg-red-50 text-red-700 border-red-200' },
      approved: { variant: 'outline', className: 'bg-green-50 text-green-700 border-green-200' },
      rejected: { variant: 'outline', className: 'bg-red-50 text-red-700 border-red-200' },
    };
    const statusLabels = {
      pending: t('taxi.statusPending', 'Pending'),
      confirmed: t('taxi.statusConfirmed', 'Confirmed'),
      in_progress: t('taxi.statusInProgress', 'In Progress'),
      completed: t('taxi.statusCompleted', 'Completed'),
      cancelled: t('taxi.statusCancelled', 'Cancelled'),
      approved: t('taxi.statusApproved', 'Approved'),
      rejected: t('taxi.statusRejected', 'Rejected'),
    };
    const c = config[status] || config.pending;
    return <Badge variant={c.variant} className={c.className}>{statusLabels[status] || status}</Badge>;
  };

  // Booking type translation helper
  const getBookingTypeLabel = (type) => {
    const types = {
      transfer: t('taxi.transfer', 'Transfer'),
      hourly: t('taxi.hourly', 'Hourly'),
      delivery: t('taxi.delivery', 'Delivery'),
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // Profile Dialog
  function renderProfileDialog() {
    return (
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('taxi.companyInfo')}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Company Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.companyName', 'Company Name')} *</Label>
                <Input
                  value={profileForm.company_name}
                  onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
                  placeholder="ABC Transport GmbH"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.contactName', 'Contact Person')} *</Label>
                <Input
                  value={profileForm.contact_name}
                  onChange={(e) => setProfileForm({ ...profileForm, contact_name: e.target.value })}
                  placeholder="Max Mustermann"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.email', 'Email')} *</Label>
                <Input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  placeholder="info@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.phone', 'Phone')} *</Label>
                <Input
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  placeholder="+49 123 456789"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('common.address', 'Address')}</Label>
              <Input
                value={profileForm.address}
                onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                placeholder="Musterstraße 123"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.city', 'City')} *</Label>
                <Input
                  value={profileForm.city}
                  onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                  placeholder="Berlin"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.country', 'Country')} *</Label>
                <Select value={profileForm.country} onValueChange={(v) => setProfileForm({ ...profileForm, country: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DE">🇩🇪 Deutschland</SelectItem>
                    <SelectItem value="TR">🇹🇷 Türkiye</SelectItem>
                    <SelectItem value="AT">🇦🇹 Österreich</SelectItem>
                    <SelectItem value="CH">🇨🇭 Schweiz</SelectItem>
                    <SelectItem value="NL">🇳🇱 Nederland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Separator />
            
            {/* Business Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.taxNumber', 'Tax Number')}</Label>
                <Input
                  value={profileForm.tax_number}
                  onChange={(e) => setProfileForm({ ...profileForm, tax_number: e.target.value })}
                  placeholder="DE123456789"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.licenseNumber', 'Transport License')}</Label>
                <Input
                  value={profileForm.license_number}
                  onChange={(e) => setProfileForm({ ...profileForm, license_number: e.target.value })}
                  placeholder="TL-2024-12345"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('common.description', 'Description')}</Label>
              <Textarea
                value={profileForm.description}
                onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                placeholder={t('taxi.descriptionPlaceholder', 'Describe your company and services...')}
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.whatsapp')}</Label>
                <Input value={profileForm.whatsapp || ''} onChange={(e) => setProfileForm({ ...profileForm, whatsapp: e.target.value })} placeholder="+90 5xx xxx xx xx" />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.secondaryPhone')}</Label>
                <Input value={profileForm.secondary_phone || ''} onChange={(e) => setProfileForm({ ...profileForm, secondary_phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('taxi.website')}</Label>
              <Input value={profileForm.website || ''} onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })} placeholder="https://" />
            </div>
            <div className="space-y-2">
              <Label>{t('taxi.customerNotes')}</Label>
              <Textarea rows={3} value={profileForm.notes || ''} onChange={(e) => setProfileForm({ ...profileForm, notes: e.target.value })} />
            </div>
            
            <Separator />
            
            {/* Payment Info */}
            <h4 className="font-medium flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              {t('taxi.paymentDetails', 'Payment Details')}
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.bankName', 'Bank Name')}</Label>
                <Input
                  value={profileForm.bank_name}
                  onChange={(e) => setProfileForm({ ...profileForm, bank_name: e.target.value })}
                  placeholder="Deutsche Bank"
                />
              </div>
              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input
                  value={profileForm.iban}
                  onChange={(e) => setProfileForm({ ...profileForm, iban: e.target.value })}
                  placeholder="DE89 3704 0044 0532 0130 00"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProfileDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleSaveProfile} className="bg-orange-500 hover:bg-orange-600">
              {t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Vehicle Dialog
  function renderVehicleDialog() {
    return (
      <Dialog open={showVehicleDialog} onOpenChange={(open) => {
        setShowVehicleDialog(open);
        if (!open) {
          setEditingVehicle(null);
          resetVehicleForm();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingVehicle ? t('taxi.editVehicle', 'Edit Vehicle') : t('taxi.addVehicle', 'Add Vehicle')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.vehicleName')} *</Label>
                <Input
                  value={vehicleForm.name}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, name: e.target.value })}
                  placeholder="Mercedes S 580 Stretch"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.vehicleClass')} *</Label>
                <Select
                  value={vehicleForm.vehicle_class}
                  onValueChange={(v) => {
                    const vc = vehicleClasses.find(c => c.id === v);
                    setVehicleForm({
                      ...vehicleForm,
                      vehicle_class: v,
                      capacity_passengers: vc?.max_passengers || vehicleForm.capacity_passengers,
                      capacity_luggage: vc?.max_luggage || vehicleForm.capacity_luggage,
                    });
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {vehicleClasses.map((vc) => {
                      const lang = (i18n.language || 'tr').slice(0, 2);
                      const className = lang === 'tr' ? (vc.name_tr || vc.name) : lang === 'de' ? (vc.name_de || vc.name) : vc.name;
                      return (
                        <SelectItem key={vc.id} value={vc.id}>
                          {className} ({vc.max_passengers} {t('taxi.passengers')})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.brand')} *</Label>
                <Input value={vehicleForm.brand} onChange={(e) => setVehicleForm({ ...vehicleForm, brand: e.target.value })} placeholder="Mercedes-Benz" />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.model')} *</Label>
                <Input value={vehicleForm.model} onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })} placeholder="S 580" />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.year')}</Label>
                <Input type="number" value={vehicleForm.year} onChange={(e) => setVehicleForm({ ...vehicleForm, year: e.target.value })} />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.licensePlate')}</Label>
                <Input value={vehicleForm.license_plate} onChange={(e) => setVehicleForm({ ...vehicleForm, license_plate: e.target.value.toUpperCase() })} placeholder="34 LMO 580" />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.color')}</Label>
                <Input value={vehicleForm.color} onChange={(e) => setVehicleForm({ ...vehicleForm, color: e.target.value })} placeholder="Siyah" />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.city')}</Label>
                <Input value={vehicleForm.city} onChange={(e) => setVehicleForm({ ...vehicleForm, city: e.target.value })} placeholder="İstanbul" />
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.passengerCapacity')}</Label>
                <Input type="number" min="1" max="50" value={vehicleForm.capacity_passengers} onChange={(e) => setVehicleForm({ ...vehicleForm, capacity_passengers: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.luggageCapacity')}</Label>
                <Input type="number" min="0" value={vehicleForm.capacity_luggage} onChange={(e) => setVehicleForm({ ...vehicleForm, capacity_luggage: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.doors')}</Label>
                <Input type="number" min="2" max="6" value={vehicleForm.doors} onChange={(e) => setVehicleForm({ ...vehicleForm, doors: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.lengthM')}</Label>
                <Input type="number" min="0" step="0.1" value={vehicleForm.length_m} onChange={(e) => setVehicleForm({ ...vehicleForm, length_m: e.target.value })} placeholder="8.5" />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.bodyStyle')}</Label>
                <Select value={vehicleForm.body_style || 'stretch'} onValueChange={(v) => setVehicleForm({ ...vehicleForm, body_style: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['stretch', 'sedan', 'suv', 'van', 'sprinter', 'partyBus', 'vintage'].map((id) => (
                      <SelectItem key={id} value={id}>{t(`taxi.${id}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.fuelType')}</Label>
                <Select value={vehicleForm.fuel_type || 'petrol'} onValueChange={(v) => setVehicleForm({ ...vehicleForm, fuel_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['petrol', 'diesel', 'hybrid', 'electric'].map((id) => (
                      <SelectItem key={id} value={id}>{t(`taxi.${id}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.transmission')}</Label>
                <Select value={vehicleForm.transmission || 'automatic'} onValueChange={(v) => setVehicleForm({ ...vehicleForm, transmission: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="automatic">{t('taxi.automatic')}</SelectItem>
                    <SelectItem value="manual">{t('taxi.manual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('taxi.chassisNumber')}</Label>
              <Input value={vehicleForm.chassis_number} onChange={(e) => setVehicleForm({ ...vehicleForm, chassis_number: e.target.value.toUpperCase() })} placeholder="WDD2221231A123456" maxLength={17} />
            </div>

            <Separator />
            <h4 className="font-medium">{t('taxi.pricing')}</h4>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.hourlyPrice')}</Label>
                <Input type="number" min="0" step="0.01" value={vehicleForm.hourly_price} onChange={(e) => setVehicleForm({ ...vehicleForm, hourly_price: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.dailyPrice')}</Label>
                <Input type="number" min="0" step="0.01" value={vehicleForm.daily_price} onChange={(e) => setVehicleForm({ ...vehicleForm, daily_price: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.currency')}</Label>
                <Select value={vehicleForm.currency || 'EUR'} onValueChange={(v) => setVehicleForm({ ...vehicleForm, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['EUR', 'TRY', 'USD', 'GBP'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.minHours')}</Label>
                <Input type="number" min="1" value={vehicleForm.min_hours} onChange={(e) => setVehicleForm({ ...vehicleForm, min_hours: e.target.value })} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.driverName')}</Label>
                <Input value={vehicleForm.driver_name} onChange={(e) => setVehicleForm({ ...vehicleForm, driver_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.driverPhone')}</Label>
                <Input value={vehicleForm.driver_phone} onChange={(e) => setVehicleForm({ ...vehicleForm, driver_phone: e.target.value })} placeholder="+90 5xx xxx xx xx" />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={!!vehicleForm.chauffeur_included} onCheckedChange={(checked) => setVehicleForm({ ...vehicleForm, chauffeur_included: checked })} />
                <Label>{t('taxi.chauffeurIncluded')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!vehicleForm.is_active} onCheckedChange={(checked) => setVehicleForm({ ...vehicleForm, is_active: checked })} />
                <Label>{t('taxi.vehicleActive')}</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('taxi.features')}</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {LIMO_FEATURES.map((id) => (
                  <label key={id} className="flex items-center gap-2 text-sm border rounded-md px-2 py-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(vehicleForm.features || []).includes(id)}
                      onChange={(e) => {
                        const next = new Set(vehicleForm.features || []);
                        if (e.target.checked) next.add(id);
                        else next.delete(id);
                        setVehicleForm({ ...vehicleForm, features: [...next] });
                      }}
                    />
                    {t(`taxi.${id}`)}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('taxi.description')}</Label>
              <Textarea
                rows={3}
                value={vehicleForm.description}
                onChange={(e) => setVehicleForm({ ...vehicleForm, description: e.target.value })}
                placeholder={t('taxi.descriptionPlaceholder')}
              />
            </div>

            {/* Vehicle Images */}
            <div className="space-y-2">
              <Label>{t('taxi.vehicleImages', 'Vehicle Images')}</Label>
              <div className="grid grid-cols-4 gap-2">
                {vehicleForm.images.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <img src={img} alt={`Vehicle ${idx + 1}`} className="w-full h-20 object-cover rounded border" />
                    <button
                      type="button"
                      onClick={() => {
                        const newImages = vehicleForm.images.filter((_, i) => i !== idx);
                        setVehicleForm({ ...vehicleForm, images: newImages });
                      }}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {vehicleForm.images.length < 6 && (
                  <label className="border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors h-20">
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-xs text-gray-500 mt-1">{t('common.upload', 'Upload')}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Convert to base64 for simplicity
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const base64 = event.target.result;
                            setVehicleForm({ 
                              ...vehicleForm, 
                              images: [...vehicleForm.images, base64] 
                            });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </label>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t('taxi.imageHint', 'Upload up to 6 images of your vehicle')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVehicleDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveVehicle} className="bg-orange-500 hover:bg-orange-600">
              {editingVehicle ? t('common.update', 'Update') : t('common.add', 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Price Profile Dialog
  function renderPriceProfileDialog() {
    return (
      <Dialog open={showPriceProfileDialog} onOpenChange={(open) => {
        setShowPriceProfileDialog(open);
        if (!open) {
          setEditingPriceProfile(null);
          resetPriceProfileForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPriceProfile ? t('taxi.editPriceProfile', 'Edit Price Profile') : t('taxi.addPriceProfile', 'Add Automatic Offer Profile')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              {t('taxi.priceProfileInfo', 'When a new request matches your conditions, the system will automatically create an offer.')}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.profileName', 'Profile Name')} *</Label>
                <Input
                  value={priceProfileForm.name}
                  onChange={(e) => setPriceProfileForm({ ...priceProfileForm, name: e.target.value })}
                  placeholder="Airport Transfer Standard"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.vehicle', 'Vehicle')} *</Label>
                <Select
                  value={priceProfileForm.vehicle_id}
                  onValueChange={(v) => setPriceProfileForm({ ...priceProfileForm, vehicle_id: v })}
                >
                  <SelectTrigger><SelectValue placeholder={t('taxi.selectVehicle', 'Select vehicle')} /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Separator />
            
            <h4 className="font-medium">{t('taxi.pricing', 'Pricing')}</h4>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.basePrice', 'Base Price')} (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={priceProfileForm.base_price}
                  onChange={(e) => setPriceProfileForm({ ...priceProfileForm, base_price: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.pricePerKm', 'Price/km')} (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={priceProfileForm.price_per_km}
                  onChange={(e) => setPriceProfileForm({ ...priceProfileForm, price_per_km: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.pricePerHour', 'Price/hour')} (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={priceProfileForm.price_per_hour}
                  onChange={(e) => setPriceProfileForm({ ...priceProfileForm, price_per_hour: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.minimumPrice', 'Minimum')} (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={priceProfileForm.minimum_price}
                  onChange={(e) => setPriceProfileForm({ ...priceProfileForm, minimum_price: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            
            <Separator />
            
            <h4 className="font-medium">{t('taxi.conditions', 'Conditions')}</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.minDistance', 'Min Distance')} (km)</Label>
                <Input
                  type="number"
                  value={priceProfileForm.min_distance_km || ''}
                  onChange={(e) => setPriceProfileForm({ ...priceProfileForm, min_distance_km: e.target.value ? parseInt(e.target.value) : null })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.maxDistance', 'Max Distance')} (km)</Label>
                <Input
                  type="number"
                  value={priceProfileForm.max_distance_km || ''}
                  onChange={(e) => setPriceProfileForm({ ...priceProfileForm, max_distance_km: e.target.value ? parseInt(e.target.value) : null })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('taxi.maxPassengers', 'Max Passengers')}</Label>
                <Input
                  type="number"
                  value={priceProfileForm.max_passengers || ''}
                  onChange={(e) => setPriceProfileForm({ ...priceProfileForm, max_passengers: e.target.value ? parseInt(e.target.value) : null })}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={priceProfileForm.is_active}
                onCheckedChange={(checked) => setPriceProfileForm({ ...priceProfileForm, is_active: checked })}
              />
              <Label>{t('taxi.profileActive', 'Profile is active')}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriceProfileDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSavePriceProfile} className="bg-orange-500 hover:bg-orange-600">
              {editingPriceProfile ? t('common.update') : t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Offer Dialog
  function renderOfferDialog() {
    if (!selectedRequest) return null;
    
    return (
      <Dialog open={showOfferDialog} onOpenChange={(open) => {
        setShowOfferDialog(open);
        if (!open) setSelectedRequest(null);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('taxi.createOffer', 'Create Offer')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Request Summary */}
            <div className="p-3 bg-gray-50 rounded-lg space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-green-600" />
                <span>{selectedRequest.pickup_address}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-red-600" />
                <span>{selectedRequest.dropoff_address || '-'}</span>
              </div>
              <div className="flex items-center gap-4 text-gray-600">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(selectedRequest.pickup_datetime).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {new Date(selectedRequest.pickup_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {selectedRequest.passengers}
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('taxi.selectVehicle', 'Select Vehicle')} *</Label>
              <Select
                value={offerForm.vehicle_id}
                onValueChange={(v) => setOfferForm({ ...offerForm, vehicle_id: v })}
              >
                <SelectTrigger><SelectValue placeholder={t('taxi.selectVehicle')} /></SelectTrigger>
                <SelectContent>
                  {vehicles.filter(v => v.is_active).map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name} ({v.brand} {v.model})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('taxi.offerPrice', 'Your Price')} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={offerForm.price}
                  onChange={(e) => setOfferForm({ ...offerForm, price: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.currency', 'Currency')}</Label>
                <Select
                  value={offerForm.currency}
                  onValueChange={(v) => setOfferForm({ ...offerForm, currency: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="TRY">₺ TRY</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('taxi.notes', 'Notes')}</Label>
              <Textarea
                value={offerForm.notes}
                onChange={(e) => setOfferForm({ ...offerForm, notes: e.target.value })}
                placeholder={t('taxi.notesPlaceholder', 'Additional information for the customer...')}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateOffer} className="bg-orange-500 hover:bg-orange-600">
              <Send className="w-4 h-4 mr-2" />
              {t('taxi.sendOffer', 'Send Offer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Main Dashboard
  return (
    <div className="space-y-6">
      <Card className={isRegistered ? '' : 'border-amber-200 bg-amber-50/60'}>
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">{t('taxi.companyInfo')}</h3>
            <p className="text-sm text-muted-foreground">
              {isRegistered
                ? [profile?.company_name, profile?.city, profile?.phone].filter(Boolean).join(' · ')
                : t('taxi.companyCardHint')}
            </p>
          </div>
          <Button variant="outline" onClick={() => setShowProfileDialog(true)}>
            {isRegistered ? t('common.edit', 'Düzenle') : t('taxi.fillCompany')}
          </Button>
        </CardContent>
      </Card>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <CarTaxiFront className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('taxi.totalVehicles', 'Vehicles')}</p>
                <p className="text-2xl font-bold">{stats?.total_vehicles || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('taxi.plannedTrips', 'Planned')}</p>
                <p className="text-2xl font-bold">{stats?.planned_trips || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('taxi.completed', 'Completed')}</p>
                <p className="text-2xl font-bold">{stats?.completed_trips || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('taxi.revenue', 'Revenue')}</p>
                <p className="text-2xl font-bold">€{stats?.total_revenue || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={loadRatingDetails}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stats?.is_top_choice ? 'bg-amber-100' : 'bg-gray-100'}`}>
                <Star className={`w-5 h-5 ${stats?.is_top_choice ? 'text-amber-600 fill-amber-500' : 'text-gray-600'}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('taxi.rating', 'Rating')}</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{stats?.rating?.toFixed(1) || '0.0'}</p>
                  {stats?.is_top_choice && (
                    <Badge className="bg-amber-500 text-white text-xs">Top</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="requests" className="gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">{t('taxi.requests', 'Requests')}</span>
          </TabsTrigger>
          <TabsTrigger value="trips" className="gap-2">
            <Car className="w-4 h-4" />
            <span className="hidden sm:inline">{t('taxi.trips', 'Trips')}</span>
          </TabsTrigger>
          <TabsTrigger value="vehicles" className="gap-2">
            <CarTaxiFront className="w-4 h-4" />
            <span className="hidden sm:inline">{t('taxi.vehicles', 'Vehicles')}</span>
          </TabsTrigger>
          <TabsTrigger value="areas" className="gap-2">
            <Layers className="w-4 h-4" />
            <span className="hidden sm:inline">{t('taxi.serviceAreas', 'Areas')}</span>
          </TabsTrigger>
          <TabsTrigger value="pricing" className="gap-2">
            <DollarSign className="w-4 h-4" />
            <span className="hidden sm:inline">{t('taxi.pricing', 'Pricing')}</span>
          </TabsTrigger>
          <TabsTrigger value="dispatcher" className="gap-2">
            <Headphones className="w-4 h-4" />
            <span className="hidden sm:inline">{t('taxi.dispatcher', 'Dispatcher')}</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <BellRing className="w-4 h-4" />
            <span className="hidden sm:inline">{t('taxi.notifications', 'Notifications')}</span>
          </TabsTrigger>
          <TabsTrigger value="book-for-other" className="gap-2">
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('taxi.bookForOther', 'Book for Other')}</span>
          </TabsTrigger>
          <TabsTrigger value="corporate" className="gap-2">
            <Building className="w-4 h-4" />
            <span className="hidden sm:inline">{t('taxi.corporate', 'Corporate')}</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">{t('taxi.settings', 'Settings')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('taxi.bookingRequests', 'Booking Requests')}</CardTitle>
                <CardDescription>{t('taxi.requestsDescription', 'New booking requests from customers')}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select value={requestFilter} onValueChange={(v) => {
                  setRequestFilter(v);
                  loadRequests();
                }}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">{t('taxi.newRequests', 'New Requests')}</SelectItem>
                    <SelectItem value="my_offers">{t('taxi.myOffers', 'My Offers')}</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={loadRequests}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {requests.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('taxi.noRequests', 'No booking requests at the moment')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('taxi.route', 'Route')}</TableHead>
                      <TableHead>{t('taxi.dateTime', 'Date & Time')}</TableHead>
                      <TableHead>{t('taxi.passengers', 'Passengers')}</TableHead>
                      <TableHead>{t('taxi.type', 'Type')}</TableHead>
                      <TableHead>{t('taxi.status', 'Status')}</TableHead>
                      <TableHead className="text-right">{t('common.actions', 'Actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                              <MapPin className="w-3 h-3 text-green-600" />
                              <span className="truncate max-w-48">{req.pickup_address}</span>
                            </div>
                            {req.dropoff_address && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <MapPin className="w-3 h-3 text-red-600" />
                                <span className="truncate max-w-48">{req.dropoff_address}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{new Date(req.pickup_datetime).toLocaleDateString()}</p>
                            <p className="text-muted-foreground">
                              {new Date(req.pickup_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            {req.passengers}
                            {req.luggage > 0 && (
                              <span className="text-muted-foreground">/ {req.luggage}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {getBookingTypeLabel(req.booking_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {req.my_offer ? (
                            <Badge className="bg-orange-100 text-orange-700">
                              €{req.my_offer.price} {t('taxi.offered', 'offered')}
                            </Badge>
                          ) : (
                            getStatusBadge(req.status)
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {!req.my_offer && (
                            <Button
                              size="sm"
                              className="bg-orange-500 hover:bg-orange-600"
                              onClick={() => {
                                setSelectedRequest(req);
                                setShowOfferDialog(true);
                              }}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              {t('taxi.offer', 'Offer')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trips Tab */}
        <TabsContent value="trips" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('taxi.myTrips', 'My Trips')}</CardTitle>
              <CardDescription>{t('taxi.tripsDescription', 'Confirmed and completed trips')}</CardDescription>
            </CardHeader>
            <CardContent>
              {trips.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('taxi.noTrips', 'No trips yet')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('taxi.booking', 'Booking')}</TableHead>
                      <TableHead>{t('taxi.route', 'Route')}</TableHead>
                      <TableHead>{t('taxi.customer', 'Customer')}</TableHead>
                      <TableHead>{t('taxi.dateTime', 'Date & Time')}</TableHead>
                      <TableHead>{t('taxi.price', 'Price')}</TableHead>
                      <TableHead>{t('taxi.status', 'Status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trips.map((trip) => (
                      <TableRow key={trip.id}>
                        <TableCell className="font-mono text-sm">{trip.booking_number}</TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-green-600" />
                              {trip.pickup_address}
                            </div>
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <MapPin className="w-3 h-3 text-red-600" />
                              {trip.dropoff_address}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{trip.contact_name}</p>
                            <p className="text-sm text-muted-foreground">{trip.contact_phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(trip.pickup_datetime).toLocaleString()}
                        </TableCell>
                        <TableCell className="font-medium">
                          {trip.currency === 'EUR' ? '€' : trip.currency}{trip.price}
                        </TableCell>
                        <TableCell>{getStatusBadge(trip.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('taxi.myVehicles', 'My Vehicles')}</CardTitle>
                <CardDescription>{t('taxi.vehiclesDescription', 'Manage your fleet')}</CardDescription>
              </div>
              <Button onClick={() => setShowVehicleDialog(true)} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="w-4 h-4 mr-2" />
                {t('taxi.addVehicle', 'Add Vehicle')}
              </Button>
            </CardHeader>
            <CardContent>
              {vehicles.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CarTaxiFront className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('taxi.noVehicles', 'No vehicles added yet')}</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {vehicles.map((vehicle) => {
                    const vcConfig = VEHICLE_CLASS_CONFIG[vehicle.vehicle_class] || VEHICLE_CLASS_CONFIG.economy;
                    const VcIcon = vcConfig.icon;
                    
                    return (
                      <Card key={vehicle.id} className="overflow-hidden">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`p-3 rounded-lg ${vcConfig.color}`}>
                                <VcIcon className="w-6 h-6" />
                              </div>
                              <div>
                                <h4 className="font-semibold">{vehicle.name}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {vehicle.brand} {vehicle.model} ({vehicle.year})
                                  {vehicle.city ? ` · ${vehicle.city}` : ''}
                                </p>
                                <div className="flex items-center gap-4 mt-1 text-sm flex-wrap">
                                  <Badge variant="outline" className="capitalize">{vehicle.vehicle_class}</Badge>
                                  <span className="flex items-center gap-1">
                                    <Users className="w-3 h-3" /> {vehicle.capacity_passengers} {t('taxi.passengers')}
                                  </span>
                                  {vehicle.hourly_price ? (
                                    <span>{vehicle.hourly_price} {vehicle.currency || 'EUR'}/{t('taxi.hour')}</span>
                                  ) : null}
                                  <span className="flex items-center gap-1">
                                    <Briefcase className="w-3 h-3" /> {vehicle.capacity_luggage}
                                  </span>
                                  {vehicle.license_plate && (
                                    <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                                      {vehicle.license_plate}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={vehicle.is_active ? 'default' : 'secondary'}>
                                {vehicle.is_active ? t('common.active') : t('common.inactive')}
                              </Badge>
                              <Button variant="ghost" size="icon" onClick={() => handleEditVehicle(vehicle)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteVehicle(vehicle.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Areas Tab */}
        <TabsContent value="areas" className="space-y-4">
          <Suspense fallback={
            <Card>
              <CardContent className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-orange-500" />
                <p className="mt-2 text-muted-foreground">{t('common.loading', 'Loading map...')}</p>
              </CardContent>
            </Card>
          }>
            <ServiceAreaMap
              areas={serviceAreas}
              onSaveArea={handleSaveServiceArea}
              onDeleteArea={handleDeleteServiceArea}
              defaultCenter={profile?.city === 'Istanbul' ? [41.0082, 28.9784] : 
                            profile?.city === 'Berlin' ? [52.52, 13.405] :
                            profile?.city === 'Munich' ? [48.1351, 11.582] :
                            [52.52, 13.405]}
              editable={true}
            />
          </Suspense>
        </TabsContent>

        {/* Pricing Tab */}
        <TabsContent value="pricing" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('taxi.automaticOffers', 'Automatic Offer Profiles')}</CardTitle>
                <CardDescription>
                  {t('taxi.pricingDescription', 'Configure automatic pricing for booking requests')}
                </CardDescription>
              </div>
              <Button 
                onClick={() => setShowPriceProfileDialog(true)} 
                className="bg-orange-500 hover:bg-orange-600"
                disabled={vehicles.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('taxi.addProfile', 'Add Profile')}
              </Button>
            </CardHeader>
            <CardContent>
              {vehicles.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('taxi.addVehicleFirst', 'Add a vehicle first to create price profiles')}</p>
                </div>
              ) : priceProfiles.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('taxi.noProfiles', 'No price profiles yet')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('taxi.profile', 'Profile')}</TableHead>
                      <TableHead>{t('taxi.vehicle', 'Vehicle')}</TableHead>
                      <TableHead>{t('taxi.pricing', 'Pricing')}</TableHead>
                      <TableHead>{t('taxi.conditions', 'Conditions')}</TableHead>
                      <TableHead>{t('taxi.status', 'Status')}</TableHead>
                      <TableHead className="text-right">{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {priceProfiles.map((profile) => {
                      const vehicle = vehicles.find(v => v.id === profile.vehicle_id);
                      return (
                        <TableRow key={profile.id}>
                          <TableCell className="font-medium">{profile.name}</TableCell>
                          <TableCell>{vehicle?.name || '-'}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>€{profile.base_price} + €{profile.price_per_km}/km</p>
                              {profile.minimum_price > 0 && (
                                <p className="text-muted-foreground">Min: €{profile.minimum_price}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-muted-foreground">
                              {profile.max_distance_km && <p>Max: {profile.max_distance_km}km</p>}
                              {profile.max_passengers && <p>Max: {profile.max_passengers} pax</p>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={profile.is_active ? 'default' : 'secondary'}>
                              {profile.is_active ? t('common.active') : t('common.inactive')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => {
                              setPriceProfileForm(profile);
                              setEditingPriceProfile(profile);
                              setShowPriceProfileDialog(true);
                            }}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeletePriceProfile(profile.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dispatcher Tab */}
        <TabsContent value="dispatcher" className="space-y-4">
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          }>
            <DispatcherPanel />
          </Suspense>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BellRing className="w-5 h-5 text-orange-500" />
                {t('taxi.notificationSettings', 'Notification Settings')}
              </CardTitle>
              <CardDescription>
                {t('taxi.notificationSettingsDesc', 'Configure SMS and email notifications for bookings and trips')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* SMS Notifications */}
              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {t('taxi.smsNotifications', 'SMS Notifications')}
                </h4>
                <div className="grid gap-4 pl-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{t('taxi.newBookingNotify', 'New Booking')}</p>
                      <p className="text-xs text-muted-foreground">{t('taxi.newBookingNotifyDesc', 'Receive SMS when a new booking request arrives')}</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{t('taxi.tripStartNotify', 'Trip Started')}</p>
                      <p className="text-xs text-muted-foreground">{t('taxi.tripStartNotifyDesc', 'Notify when driver starts the trip')}</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{t('taxi.tripCompleteNotify', 'Trip Completed')}</p>
                      <p className="text-xs text-muted-foreground">{t('taxi.tripCompleteNotifyDesc', 'Notify when trip is completed')}</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Email Notifications */}
              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {t('taxi.emailNotifications', 'Email Notifications')}
                </h4>
                <div className="grid gap-4 pl-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{t('taxi.dailySummary', 'Daily Summary')}</p>
                      <p className="text-xs text-muted-foreground">{t('taxi.dailySummaryDesc', 'Receive daily summary of bookings and revenue')}</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{t('taxi.weeklyReport', 'Weekly Report')}</p>
                      <p className="text-xs text-muted-foreground">{t('taxi.weeklyReportDesc', 'Detailed weekly performance report')}</p>
                    </div>
                    <Switch />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{t('taxi.customerFeedback', 'Customer Feedback')}</p>
                      <p className="text-xs text-muted-foreground">{t('taxi.customerFeedbackDesc', 'Notify when customers leave reviews')}</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Test Notification */}
              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                <div>
                  <p className="font-medium">{t('taxi.testNotification', 'Test Notification')}</p>
                  <p className="text-sm text-muted-foreground">{t('taxi.testNotificationDesc', 'Send a test SMS to verify your settings')}</p>
                </div>
                <Button variant="outline" className="border-orange-200 text-orange-600 hover:bg-orange-100">
                  <Send className="w-4 h-4 mr-2" />
                  {t('taxi.sendTest', 'Send Test')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Book for Other Tab */}
        <TabsContent value="book-for-other" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-orange-500" />
                {t('taxi.bookForOther', 'Book for Someone Else')}
              </CardTitle>
              <CardDescription>
                {t('taxi.bookForOtherDesc', 'Create transfer bookings on behalf of other people. They will receive notifications about their ride.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => setShowBookForOtherDialog(true)}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('taxi.newBookingForOther', 'New Booking')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Corporate Account Tab */}
        <TabsContent value="corporate" className="space-y-4">
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          }>
            <CorporateAccountPanel />
          </Suspense>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('taxi.carrierSettings', 'Carrier Settings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {profile ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <UserCircle className="w-5 h-5" />
                      {profile.company_name}
                    </h4>
                    <Button variant="outline" size="sm" onClick={() => setShowProfileDialog(true)}>
                      <Edit2 className="w-4 h-4 mr-2" />
                      {t('common.edit')}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t('taxi.contactName')}</p>
                      <p>{profile.contact_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('common.email')}</p>
                      <p>{profile.email}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('common.phone')}</p>
                      <p>{profile.phone}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('common.location')}</p>
                      <p>{profile.city}, {profile.country}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-2">{t('taxi.status')}</p>
                    {getStatusBadge(profile.status)}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">{t('taxi.companyCardHint')}</p>
                  <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setShowProfileDialog(true)}>
                    {t('taxi.fillCompany')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {renderProfileDialog()}
      {renderVehicleDialog()}
      {renderPriceProfileDialog()}
      {renderOfferDialog()}
      
      {/* Book for Other Dialog */}
      <Suspense fallback={null}>
        <BookForOtherForm 
          isOpen={showBookForOtherDialog}
          onClose={() => setShowBookForOtherDialog(false)}
          onSuccess={() => {
            loadRequests();
            loadTrips();
          }}
        />
      </Suspense>
      
      {/* Rating Details Dialog */}
      <Dialog open={showRatingDialog} onOpenChange={setShowRatingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('taxi.ratingDetails', 'Rating Details')}</DialogTitle>
          </DialogHeader>
          {ratingDetails && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Star className="w-8 h-8 text-amber-500 fill-amber-500" />
                  <span className="text-4xl font-bold">{ratingDetails.rating?.toFixed(1) || '0.0'}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('taxi.basedOnRatings', 'Based on {{count}} ratings', { count: ratingDetails.total_ratings || 0 })}
                </p>
              </div>
              
              <Separator />
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span>{t('taxi.communication', 'Communication')}</span>
                  <span className="font-medium">{ratingDetails.rating_breakdown?.communication?.toFixed(1) || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>{t('taxi.driver', 'Driver')}</span>
                  <span className="font-medium">{ratingDetails.rating_breakdown?.driver?.toFixed(1) || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>{t('taxi.vehicle', 'Vehicle')}</span>
                  <span className="font-medium">{ratingDetails.rating_breakdown?.vehicle?.toFixed(1) || '-'}</span>
                </div>
              </div>
              
              <Separator />
              
              {/* Top Choice Status */}
              <div className="p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Award className={`w-5 h-5 ${ratingDetails.top_choice?.is_active ? 'text-amber-600' : 'text-gray-400'}`} />
                  <span className="font-medium">Top-Auswahl Status</span>
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    {ratingDetails.top_choice?.requirements?.current_rating >= 4.5 ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <X className="w-4 h-4 text-red-500" />
                    )}
                    <span>Rating ≥ 4.5 ({ratingDetails.top_choice?.requirements?.current_rating?.toFixed(1)})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {ratingDetails.top_choice?.requirements?.current_trips >= 5 ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <X className="w-4 h-4 text-red-500" />
                    )}
                    <span>≥ 5 completed trips ({ratingDetails.top_choice?.requirements?.current_trips})</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxiLimousineTab;
